// ---------------------------------------------------------------------------
// PWA_ControlBillet — Setup / configuration screen (initial wizard)
// Configures event info, vigile identity, and preferences.
// ---------------------------------------------------------------------------

import { useState, useEffect } from 'react';
import { useConfigStore } from '@/store/config-store';
import { useAppStore } from '@/store/app-store';
import { useAuthStore } from '@/store/auth-store';
import { Toggle } from '@/components/Toggle';

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function SetupScreen() {
  const config = useConfigStore();
  const showToast = useAppStore((s) => s.showToast);
  const navigate = useAppStore((s) => s.navigate);
  const user = useAuthStore((s) => s.user);

  // Form state — loaded from store on mount.
  const [eventId, setEventId] = useState('');
  const [eventDate, setEventDate] = useState('');
  const [vigileId, setVigileId] = useState('');
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [vibrationEnabled, setVibrationEnabled] = useState(true);
  const [autoSync, setAutoSync] = useState(true);

  // Load existing config on mount.
  useEffect(() => {
    const s = useConfigStore.getState();
    setEventId(s.eventId);
    setEventDate(s.eventDate);
    setVigileId(s.vigileId || (user ? `${user.first_name} ${user.last_name}`.trim() : ''));
    setSoundEnabled(s.soundEnabled);
    setVibrationEnabled(s.vibrationEnabled);
    setAutoSync(s.autoSync);
  }, [user]);

  // Save and proceed.
  function handleSave() {
    if (!eventId || !eventDate) {
      showToast('Veuillez remplir tous les champs obligatoires', 'warning');
      return;
    }

    config.setConfig({
      eventId,
      eventDate,
      vigileId,
      soundEnabled,
      vibrationEnabled,
      autoSync,
    });

    showToast('Configuration enregistree', 'success');
    navigate('scanner');
  }

  const canSave = eventId !== '' && eventDate !== '';

  return (
    <div className="h-full w-full flex items-start justify-center bg-surface overflow-y-auto py-6 px-4">
      <div className="bg-surface-card rounded-2xl p-6 w-full max-w-lg space-y-5 shadow-2xl">
        {/* Title */}
        <h1 className="text-xl font-bold text-white text-center">
          Configuration de l&apos;evenement
        </h1>
        <p className="text-gray-400 text-xs text-center -mt-2">
          Configurez les parametres de l&apos;evenement a scanner
        </p>

        {/* Event ID */}
        <div className="space-y-1">
          <label className="text-sm text-gray-400">
            ID Evenement <span className="text-danger">*</span>
          </label>
          <input
            type="number"
            value={eventId}
            onChange={(e) => setEventId(e.target.value)}
            placeholder="12345"
            className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent text-sm"
          />
        </div>

        {/* Event Date */}
        <div className="space-y-1">
          <label className="text-sm text-gray-400">
            Date de l&apos;evenement <span className="text-danger">*</span>
          </label>
          <input
            type="date"
            value={eventDate}
            onChange={(e) => setEventDate(e.target.value)}
            className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent text-sm [color-scheme:dark]"
          />
        </div>

        {/* Vigile Name */}
        <div className="space-y-1">
          <label className="text-sm text-gray-400">Nom du vigile</label>
          <input
            type="text"
            value={vigileId}
            onChange={(e) => setVigileId(e.target.value)}
            placeholder="Jean Dupont"
            className="w-full bg-surface border border-gray-700 rounded-xl px-4 py-3 text-white placeholder-gray-600 focus:outline-none focus:border-accent text-sm"
          />
        </div>

        {/* Separator */}
        <div className="border-t border-white/10 pt-4">
          <p className="text-xs text-gray-500 font-medium mb-3 uppercase tracking-wider">
            Preferences
          </p>
          <div className="space-y-2">
            <Toggle
              label="Sons actives"
              enabled={soundEnabled}
              onChange={setSoundEnabled}
            />
            <Toggle
              label="Vibrations activees"
              enabled={vibrationEnabled}
              onChange={setVibrationEnabled}
            />
            <Toggle
              label="Sync automatique"
              enabled={autoSync}
              onChange={setAutoSync}
            />
          </div>
        </div>

        {/* Action button */}
        <div className="pt-2">
          <button
            type="button"
            onClick={handleSave}
            disabled={!canSave}
            className="w-full btn-primary disabled:opacity-40 disabled:cursor-not-allowed text-sm"
          >
            Demarrer le scanner
          </button>
        </div>
      </div>
    </div>
  );
}
